/**
 * Phase 348 — Global Expense Categories management.
 *
 * The categories catalogue (built-in + custom) lives at /api/expense-categories
 * and is shared across every building. This page hosts the CRUD UI previously
 * embedded inside each building's "Categories" tab so the catalogue can be
 * curated from a single central location (Settings → Expense Categories).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '../components/ui/select';
import { Plus, PencilSimple, Trash, ArrowLeft, MagnifyingGlass, Tag } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ExpenseCategories() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {category_code, name, group, usage_count, isNew}
  const [form, setForm] = useState({ name: '', group: 'monthly', category_code: '' });

  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/expense-categories`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => {
    setEditing({ isNew: true });
    setForm({ name: '', group: 'monthly', category_code: '' });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    const name = c.name || t(`cat_${c.category_code}`) || '';
    setEditing({ ...c, name, isNew: false });
    setForm({ name, group: c.group || 'monthly', category_code: c.category_code });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t('fillRequiredFields') || 'Fill required fields'); return; }
    try {
      if (editing?.isNew) {
        const payload = { name: form.name.trim(), group: form.group };
        if (form.category_code && form.category_code.trim()) payload.category_code = form.category_code.trim();
        await axios.post(`${API}/expense-categories`, payload);
      } else {
        await axios.put(`${API}/expense-categories/${editing.category_code}`, { name: form.name.trim(), group: form.group });
      }
      toast.success(t('savedSuccess') || 'Saved');
      setDialogOpen(false);
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/expense-categories/${deleting.category_code}`);
      toast.success(t('deletedSuccess') || 'Deleted');
      setDelOpen(false); setDeleting(null);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
    }
  };

  // Phase 348 — Search across code + (translated/custom) name.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(c => {
      if (!q) return true;
      const code = String(c.category_code || '').toLowerCase();
      const name = (c.name || t(`cat_${c.category_code}`) || '').toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [rows, search, t]);

  const monthly = filtered.filter(c => (c.group || 'monthly') === 'monthly');
  const capital = filtered.filter(c => c.group === 'capital');

  const renderTable = (data, label, key) => (
    <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid={`expense-cat-section-${key}`}>
      <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
        <h3 className="text-xs font-bold tracking-wider uppercase text-zinc-700">{label}</h3>
        <span className="text-[11px] text-zinc-400 font-mono">{data.length}</span>
      </div>
      <table className="w-full text-sm" data-testid={`expense-cat-table-${key}`}>
        <thead>
          <tr className="bg-white border-b border-zinc-200">
            <th className="px-4 py-2.5 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-24">{t('code') || 'Code'}</th>
            <th className="px-4 py-2.5 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider">{t('name') || 'Name'}</th>
            <th className="px-4 py-2.5 text-right text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-28">{t('usage') || 'Usage'}</th>
            {isAdmin && <th className="px-4 py-2.5 text-right text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-36">{t('actions') || 'Actions'}</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={isAdmin ? 4 : 3} className="px-4 py-8 text-center text-zinc-400 text-xs">{t('noCategories') || t('noData') || 'No data'}</td></tr>
          ) : data.map(c => {
            const used = c.usage_count || 0;
            const displayName = c.name || t(`cat_${c.category_code}`) || '-';
            return (
              <tr key={c.category_code} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`expense-cat-row-${c.category_code}`}>
                <td className="px-4 py-2.5 font-mono font-semibold text-zinc-800">{c.category_code}</td>
                <td className="px-4 py-2.5 text-zinc-700">{displayName}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-500">{used}</td>
                {isAdmin && (
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-800"
                        title={t('edit') || 'Edit'}
                        data-testid={`expense-cat-edit-${c.category_code}`}
                        onClick={() => openEdit(c)}
                      ><PencilSimple size={14} /></button>
                      <button
                        className={`p-1 ${used > 0 ? 'text-zinc-300 cursor-not-allowed' : 'hover:bg-red-50 text-red-400 hover:text-red-600'}`}
                        title={used > 0 ? (t('cannotDeleteInUse') || 'In use') : (t('delete') || 'Delete')}
                        data-testid={`expense-cat-delete-${c.category_code}`}
                        onClick={() => {
                          if (used > 0) { toast.error(t('cannotDeleteInUse') || 'In use'); return; }
                          setDeleting(c); setDelOpen(true);
                        }}
                      ><Trash size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="expense-categories-page">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="expense-cat-back"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Settings'}</span>
        </button>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Tag size={20} weight="fill" className="text-zinc-700" />
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('referenceCategories') || 'Reference Categories'}
              </h1>
              <p className="text-xs text-zinc-500" data-testid="reference-cats-desc">
                {t('referenceCategoriesDesc') || 'Helper labels for organizing bank transactions and movement references. These are NOT official expenses and do NOT appear in VAT reports. Use Finance → Expenses for receipted, VAT-deductible records.'}
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button size="sm" className="bg-zinc-900 rounded-none" data-testid="expense-cat-add-btn" onClick={openAdd}>
              <Plus size={14} className="mr-1" />{t('addCategory') || 'Add Category'}
            </Button>
          )}
        </div>

        <div className="relative mb-4">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchCategoryPh') || 'Search by code or name…'}
            className="pl-8 rounded-none"
            data-testid="expense-cat-search"
          />
        </div>

        {loading ? (
          <div className="text-zinc-400 text-sm py-12 text-center">{t('loading') || 'Loading…'}</div>
        ) : (
          <div className="space-y-6">
            {renderTable(monthly, t('monthlyExpenses') || 'Monthly Expenses', 'monthly')}
            {renderTable(capital, t('capitalExpenses') || 'Capital Expenses', 'capital')}
          </div>
        )}

        {/* Add / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-none max-w-md" data-testid="expense-cat-dialog">
            <DialogHeader>
              <DialogTitle>
                {editing?.isNew ? (t('addCategory') || 'Add Category') : `${t('edit') || 'Edit'}: ${editing?.category_code || ''}`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t('name') || 'Name'}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="mt-1 rounded-none"
                  data-testid="expense-cat-name-input"
                  placeholder={t('newCategoryNamePrompt') || 'Category name…'}
                />
              </div>
              <div>
                <Label className="text-xs">{t('type') || 'Type'}</Label>
                <Select
                  value={form.group}
                  onValueChange={(v) => setForm(f => ({ ...f, group: v }))}
                  disabled={!editing?.isNew && (editing?.usage_count || 0) > 0}
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="expense-cat-group-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t('monthly') || 'Monthly'}</SelectItem>
                    <SelectItem value="capital">{t('capital') || 'Capital'}</SelectItem>
                  </SelectContent>
                </Select>
                {!editing?.isNew && (editing?.usage_count || 0) > 0 && (
                  <p className="text-[11px] text-zinc-500 mt-1">{t('cannotChangeTypeInUse') || 'Type cannot be changed while category is in use.'}</p>
                )}
              </div>
              {editing?.isNew && (
                <div>
                  <Label className="text-xs">
                    {t('code') || 'Code'} <span className="text-zinc-400 text-[10px]">({t('optional') || 'optional'})</span>
                  </Label>
                  <Input
                    value={form.category_code || ''}
                    onChange={(e) => setForm(f => ({ ...f, category_code: e.target.value.replace(/[^0-9]/g, '') }))}
                    className="mt-1 rounded-none font-mono"
                    data-testid="expense-cat-code-input"
                    placeholder={form.group === 'capital' ? '311' : '207'}
                    maxLength={4}
                  />
                  <p className="text-[11px] text-zinc-500 mt-1">{t('codeAutoGenerateHint') || 'Leave blank to auto-generate the next available code.'}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setDialogOpen(false)} data-testid="expense-cat-cancel-btn">{t('cancel') || 'Cancel'}</Button>
              <Button className="bg-zinc-900 rounded-none" onClick={handleSave} data-testid="expense-cat-save-btn">{t('save') || 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('delete') || 'Delete'}: {deleting?.category_code}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDelete') || 'Are you sure?'}
                {deleting?.name && (
                  <span className="block mt-2 text-xs text-zinc-500">
                    <span className="font-mono font-semibold">{deleting.category_code}</span> — {deleting.name}
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="expense-cat-delete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="expense-cat-delete-confirm" onClick={handleDelete}>
                {t('delete') || 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
